import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { find } from 'lodash'
import Link from 'next/link'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'

import { orderStatusesQuery } from '~/gql/queries'
import { useModalManager } from '~/hooks'
import type { ModalManagerProps } from '~/hooks/use-modal-manager'
import Button from '~/truck/button'
import Label from '~/truck/label'
import theme from '~/truck/theme'
import { getSizeStyle } from '~/truck/utils'
import { notifySentry } from '~/utils'
import { images } from '~/utils/constants'

import { DialogHeader } from '../dialog/header'
import { DialogModal } from '../dialog/modal'
import OrderStatus from '../order-status'

interface Props extends ModalManagerProps {
  orderStatus: string
  orderId: string
  onClose: (withError?: boolean) => void
}

const StyledContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 3px;
  box-shadow:
    0px 16px 24px rgba(0, 0, 0, 0.14),
    0px 6px 30px rgba(0, 0, 0, 0.12),
    0px 8px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacing.cozy}px;
  padding-bottom: ${props => props.theme.spacing.cozy * 3}px;
`

const StyledContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 0 ${props => props.theme.spacing.cozy * 3.5}px;

  > img {
    margin-bottom: ${props => props.theme.spacing.relaxed}px;

    ${getSizeStyle(theme.spacing.cozy * 6)};

    ${up('lg')} {
      ${getSizeStyle(theme.spacing.cozy * 9)};
    }
  }

  > ${Label}:not(:last-child) {
    margin-bottom: ${props => props.theme.spacing.cozy}px;
  }
`

const StyledButton = styled(Button)`
  margin-left: auto;
  margin-right: auto;
  margin-top: ${theme.spacing.cozy * 4}px;
  padding: ${theme.spacing.cozy * 1.5}px ${theme.spacing.relaxed}px;
  width: fit-content;
`

const OrderToReviewModal = (props: Props) => {
  const { orderStatus, orderId, onClose, modalId } = props

  const { closeModal } = useModalManager()

  const handleClose = () => {
    onClose()

    closeModal(modalId)
  }

  const { data, loading, error } = useQuery(orderStatusesQuery)

  if (loading) {
    return null
  }

  if (error || !data?.vendor?.orderStatuses) {
    console.error('Error al cargar los estados de pedido:', error)

    handleClose()

    return null
  }

  const orderToReviewStatus = find(data.vendor.orderStatuses, status => status.slug === orderStatus)

  if (!orderToReviewStatus) {
    console.error('No se encontró el estado de pedido:', orderStatus)

    notifySentry(error)

    handleClose()

    return null
  }

  return (
    <DialogModal onClose={handleClose}>
      <StyledContainer>
        <DialogHeader closable onClose={handleClose} closeIconSize="md" closeIconColor="black30" />
        <StyledContent>
          <img alt="icon" src={images.ILLUSTRATION_RATING} />
          <Label as="h4" $textStyle="h4Semibold" $textAlign="center">
            Califica tus pedidos
          </Label>
          <Label as="p" $color="grayscale60" $textAlign="center">
            Tu último pedido en estado <OrderStatus name={orderToReviewStatus.name} color={orderToReviewStatus.color} />{' '}
            espera tu calificación.
          </Label>
        </StyledContent>
        <Link href={`/mi-cuenta/pedidos/${orderId}`}>
          <StyledButton onClick={handleClose}>Ver pedido</StyledButton>
        </Link>
      </StyledContainer>
    </DialogModal>
  )
}

export default OrderToReviewModal
